import { Edit as EditIcon } from '@carbon/icons-react';
import type { UserProfile } from '@repo/shared/orpc/types';
import { toHoverColor, useTheme } from '@repo/theme';
import { createThemedStyles } from '@repo/theme/styles';
import { HorizBox, HoverView, PadBox } from 'component/basics';
import { ProfilePhoto } from 'component/people';
import { useTranslation } from 'component/translation';
import { getFontSize } from 'platform-specific/font';
import { Text } from 'react-native';
import { usePersonaKey } from 'util/datastore';
import type { TLabel } from 'util/stdtypes';

export type ComposerButtonProps = {
    placeholderLabel?: TLabel;
    testID?: string;
    placeholderText?: string;
    formatParams?: Record<string, any>;
    profile: UserProfile | null;
    showPhoto?: boolean;
    onPress?: () => void;
    disabled?: boolean;
};

export function ComposerButton({
    placeholderLabel,
    testID,
    placeholderText,
    formatParams,
    profile,
    onPress,
    disabled,
}: ComposerButtonProps) {
    const { colors } = useTheme();
    const s = useComposerButtonStyles();
    const tPlaceholder = useTranslation(placeholderLabel, formatParams);
    const personaKey = usePersonaKey();
    return (
        <HoverView
            ariaLabel={placeholderLabel}
            ariaText={placeholderText}
            disabled={disabled}
            hoverStyle={s.hover}
            onPress={onPress}
            role='button'
            style={[s.composerButton, disabled ? s.buttonDisabled : null]}
            testID={testID}
        >
            <HorizBox center>
                {personaKey && (
                    <PadBox right={10}>
                        <ProfilePhoto faint={disabled} profile={profile} type='tiny' />
                    </PadBox>
                )}
                <Text style={[s.composerButtonText, disabled ? s.textDisabled : null]}>{placeholderText ?? tPlaceholder}</Text>
            </HorizBox>
            <EditIcon
                color={disabled ? colors.colorTextDisabled : colors.colorTextPrimary}
                size={24}
                style={{ flexShrink: 0, paddingLeft: 16 }}
            />
        </HoverView>
    );
}

const useComposerButtonStyles = createThemedStyles((tokens) => ({
    composerButtonText: {
        color: tokens.colors.colorTextPrimary,
        fontFamily: tokens.fonts.fontFamilySansMedium,
        fontSize: getFontSize(14),
    },
    composerButton: {
        borderColor: tokens.colors.colorBorderPrimary,
        backgroundColor: tokens.colors.colorButtonSecondary,
        borderWidth: 1,
        borderRadius: 32,
        paddingHorizontal: 16,
        height: 50,
        fontFamily: tokens.fonts.fontFamilySansRegular,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        overflow: 'hidden',
    },
    hover: {
        borderColor: toHoverColor(tokens.colors.colorBorderSelected),
    },
    textDisabled: {
        color: tokens.colors.colorTextDisabled,
    },
    buttonDisabled: {
        backgroundColor: tokens.colors.colorButtonDisabled,
        borderColor: tokens.colors.colorBorderPrimary,
    },
}));
